// `/admin/audit-log` (croqui `admin/audit-log.tsx`). Foco: **a tabela**.
//
// Esta é a rota-PAI: ela desenha a trilha e, por cima, o `RouteDrawer` com o `<Outlet/>`
// da rota `$id` (`spec/24` §1 decisão 5, frame `LinhaAuditoria` do croqui). Deep link em
// `/admin/audit-log/<uuid>` monta a tabela embaixo e o drawer aberto; fechar volta para
// cá preservando os filtros. Mesma justificativa de `admin.kyc.tsx` (WEB-73 item 1) e de
// `admin.reconciliation.tsx` (WEB-78 item 1).
//
// Sem step-up: a leitura da trilha não está em `ADMIN_STEP_UP_ROUTES`.

import { useInfiniteQuery } from '@tanstack/react-query';
import { createFileRoute, Outlet, useMatch, useNavigate } from '@tanstack/react-router';
import { z } from 'zod';
import { Panel } from '../components/kit/panel.tsx';
import { RouteDrawer } from '../components/kit/route-drawer.tsx';
import { useAuditDrawerHeader } from '../features/admin-audit/audit-detail.tsx';
import { AuditFiltersBar } from '../features/admin-audit/audit-filters.tsx';
import { AuditTable } from '../features/admin-audit/audit-table.tsx';
import {
  DEFAULT_PERIOD,
  isAuditAction,
  isPeriod,
  periodFrom,
  targetFilterOf,
} from '../features/admin-audit/model.ts';
import { type AuditAction, auditListOptions } from '../features/admin-audit/queries.ts';

/**
 * Os filtros vivem na URL (`spec/17` §3): recarregar e compartilhar o link continuam
 * valendo. `period` é `.optional()` e não `.default()`, com `default` o `navigate` de
 * fechar o drawer escreveria `?period=7d` na URL e o e2e deixaria de casar
 * `/admin/audit-log$`.
 *
 * `z.custom` e não `z.enum` para a ação: o catálogo é o enum GERADO em `types/admin.d.ts`
 * (um tipo, não um valor), `isAuditAction` é a ponte, e o typecheck cobre a lista.
 *
 * Mora na rota-PAI porque o `$id` é filho dela e herda o mesmo `search`.
 */
const searchSchema = z.object({
  action: z
    .custom<AuditAction>((value) => typeof value === 'string' && isAuditAction(value))
    .optional()
    .catch(undefined),
  q: z.string().min(1).optional().catch(undefined),
  period: z.string().refine(isPeriod).optional().catch(undefined),
});

/** Largura do drawer no croqui (`width={480}`). */
const DRAWER_WIDTH = 480;

function AuditLog() {
  const search = Route.useSearch();
  const navigate = useNavigate();

  const filters = {
    ...(search.action === undefined ? {} : { action: search.action }),
    ...(search.q === undefined ? {} : { q: search.q }),
    period: search.period ?? DEFAULT_PERIOD,
  };
  const filtered = search.action !== undefined || search.q !== undefined;

  const list = useInfiniteQuery(auditListOptions(filters, periodFrom, targetFilterOf));
  const rows = list.data?.pages.flatMap((page) => page.data) ?? [];

  // A rota `$id` é filha desta: se ela casa, a linha está aberta por cima da trilha.
  const detail = useMatch({ from: '/admin/audit-log/$id', shouldThrow: false });
  const header = useAuditDrawerHeader(detail?.params.id);

  return (
    <>
      <Panel className="min-w-0 flex-1">
        <AuditFiltersBar
          filters={filters}
          count={rows.length}
          hasMore={list.hasNextPage}
          onChange={(next) => {
            void navigate({
              to: '/admin/audit-log',
              search: {
                ...(next.action === undefined ? {} : { action: next.action }),
                ...(next.q === undefined ? {} : { q: next.q }),
                ...(next.period === DEFAULT_PERIOD ? {} : { period: next.period }),
              },
              replace: true,
            });
          }}
        />
        <AuditTable
          rows={rows}
          isLoading={list.isPending}
          error={list.error}
          onRetry={() => {
            void list.refetch();
          }}
          hasMore={list.hasNextPage}
          isLoadingMore={list.isFetchingNextPage}
          onLoadMore={() => {
            void list.fetchNextPage();
          }}
          filtered={filtered}
          {...(detail === undefined ? {} : { selectedId: detail.params.id })}
          onSelect={(row) => {
            // `search` explícito: os filtros têm de sobreviver à abertura do drawer.
            void navigate({ to: '/admin/audit-log/$id', params: { id: row.id }, search });
          }}
        />
      </Panel>

      <RouteDrawer
        open={detail !== undefined}
        onClose={() => {
          void navigate({ to: '/admin/audit-log', search });
        }}
        title={header.title}
        {...(header.sub === undefined ? {} : { sub: header.sub })}
        width={DRAWER_WIDTH}
      >
        <Outlet />
      </RouteDrawer>
    </>
  );
}

export const Route = createFileRoute('/admin/audit-log')({
  validateSearch: searchSchema,
  component: AuditLog,
});
